/**
 * The reward function, decomposed into named terms.
 *
 * ===========================================================================
 * WHY TERMS AND NOT ONE SUM
 * ===========================================================================
 *
 * A single `reward(state) -> number` is undebuggable. When a run collapses the
 * only question worth asking is WHICH contribution the policy started farming,
 * and a scalar cannot answer it. Every term below is a separate object with its
 * own name, its own weight and its own pure `compute`, so the trainer can chart
 * each one per iteration (`IterationStats.rewardTerms`) and a test can construct
 * a scenario per term where the sign is known in advance.
 *
 * ===========================================================================
 * SIGN CONVENTION
 * ===========================================================================
 *
 * COUNTING terms (goals, demolitions, elapsed time) return a NON-NEGATIVE
 * magnitude and carry their sign in the weight — `goalConceded` returns "how
 * many were conceded" and is weighted -10. SIGNED terms (the two dense shaping
 * terms, the touch quality, the boost economy) return a value whose sign is
 * intrinsic to the quantity and are weighted positive.
 *
 * Mixing the two silently is how a negated weight ends up rewarding what it was
 * meant to punish, so which kind a term is stated on every term below.
 *
 * ===========================================================================
 * WHY THE DENSE TERMS CANNOT BE FARMED
 * ===========================================================================
 *
 * `ballToOpponentGoalDistance` is a POTENTIAL DIFFERENCE: each step it returns
 * (previous distance - current distance), so its sum over a whole episode
 * telescopes to (distance at episode start - distance at episode end),
 * regardless of how many steps the episode ran or how much the ball moved in
 * between. It is bounded by roughly 0.8 * its weight for ANY episode length,
 * which is what makes it safe to give a large weight. This is Ng-style
 * potential-based shaping and the property is worth preserving in any edit:
 * a term of the form "reward for being close" instead of "reward for getting
 * closer" is farmable by parking.
 *
 * `playerToBallVelocity` and `timePenalty` are per-SECOND, not per-step, so
 * their totals do not change if the frame skip changes. Both are weighted so
 * that their maximum possible accumulation over a default episode is several
 * times smaller than a single goal. `test/rl/reward.test.ts` measures that
 * empirically rather than trusting the arithmetic here.
 *
 * These weights are RL hyperparameters. Nothing in this file is a physics
 * constant; the physics constants it divides by are imported.
 */

import { GOAL_CENTER } from '../physics/arena-data';
import type { CarState } from '../physics/car-state';
import { BOOST_MAX, CAR_MAX_SPEED, TICK_RATE } from '../physics/constants';
import { clamp, type Vec3 } from '../physics/math';
import { ARENA_SPAN } from './obs';
import type { RewardContext, RewardTerm } from './types';

// ---------------------------------------------------------------------------
// What a term is given
// ---------------------------------------------------------------------------

/** The post-step world, narrowed. A `World` satisfies it structurally. */
export interface RewardWorldView {
  readonly ball: { position: Vec3; velocity: Vec3 };
  readonly cars: readonly CarState[];
}

/** One goal that happened inside the window. */
export interface WindowGoal {
  /** The team that the goal counts FOR. */
  team: 0 | 1;
  /** Car credited with the goal, or null when it was an own goal. */
  scorer: number | null;
  /** Car whose touch actually put it in, whichever team that car is on. */
  lastTouch: number | null;
}

export interface WindowDemolition {
  attacker: number;
  victim: number;
}

/**
 * Everything that happened during one action-repeat window, aggregated across
 * its ticks. Built by ./env; a test can build one by hand.
 */
export interface RewardWindow {
  /** Ticks the window actually advanced. Shorter than the frame skip on the
   *  step that ends an episode. */
  ticks: number;
  goals: readonly WindowGoal[];
  demolitions: readonly WindowDemolition[];
  /** Per car index: did this car touch the ball anywhere in the window? */
  touched: readonly boolean[];
  /** Per car index: boost collected from pads during the window. */
  boostGained: readonly number[];
  /**
   * The car that most recently touched the ball, as of the end of the window —
   * i.e. whose touch the ball's current trajectory is the consequence of.
   *
   *   a number    that car owns the ball's movement this window
   *   null        nobody has touched the ball yet this episode
   *   undefined   the caller does not track it; every car is credited, which is
   *               how `ballToOpponentGoalDistance` behaved before attribution
   *               existed. Hand-built windows in tests take this branch.
   */
  lastTouch?: number | null;
}

/** Per-car state at the START of the window, for the dense terms. */
export interface RewardCarSnapshot {
  position: Vec3;
  velocity: Vec3;
  boostAmount: number;
  demolished: boolean;
  /** Did this car touch the ball during the PREVIOUS window? Distinguishes
   *  winning possession from continuing to dribble. */
  touchedBall: boolean;
}

export interface RewardSnapshot {
  ballPosition: Vec3;
  ballVelocity: Vec3;
  cars: readonly RewardCarSnapshot[];
}

/**
 * ./types declares `world` and `events` as `unknown` so that nothing in the
 * shared contract depends on the simulation's types. This narrows both and adds
 * the window's start state, which is what makes every dense term PURE: a term
 * never keeps memory between calls, so it can be tested by handing it two
 * hand-built states.
 */
export interface RlRewardContext extends RewardContext {
  readonly world: RewardWorldView;
  readonly events: RewardWindow;
  readonly previous: RewardSnapshot;
}

/** A `RewardTerm` whose `compute` sees the narrowed context. */
export interface RlRewardTerm extends RewardTerm {
  compute(context: RlRewardContext): number;
}

// ---------------------------------------------------------------------------
// Tuning — RL hyperparameters, not physics
// ---------------------------------------------------------------------------

/**
 * One-off bonus on the first window of a new possession, on top of the directed
 * quality below. Without it, early policies have nothing at all to climb
 * toward: a random policy's ball-velocity gain is symmetric noise, but "did you
 * reach the ball" is not.
 *
 * Small, because it is the ONE thing here a good player earns many times per
 * episode. "New possession" only means the previous window had no touch, so a
 * dribble that touches every other window collects it every other window; a
 * large bonus here is a genuine farm and was measured as one.
 *
 * CONDITIONAL as of the directional fix below — paid only on a touch that did
 * not send the ball backwards. Unconditional, it was the whole bug: measured
 * over 150 kickoff episodes of the 12.3M-step policy, the directional part of a
 * TYPICAL touch was worth +-0.051 weighted against a bonus of 0.0625 paid
 * whatever direction the ball went, so a backwards touch still netted +0.011.
 * 37% of that policy's touches were backwards and every one of them paid.
 */
const TOUCH_POSSESSION_BONUS = 0.25;

/**
 * The change in the ball's goal-ward speed that scores a full-value touch.
 *
 * The yardstick used to be `BALL_MAX_SPEED` (6000), which is the wrong scale by
 * a factor of several: it is the ball's terminal speed, not the speed a car
 * imparts, and dividing by it squashed every touch a policy can actually make
 * into the bottom sixth of the range. Measured over the 12.3M-step policy, the
 * mean |quality| of a real touch came out at 0.20 — 0.051 once weighted, which
 * is SMALLER than the unconditional possession bonus sitting next to it. The
 * direction of a touch was worth less than the fact of it.
 *
 * 2000 uu/s is roughly what a committed drive through the ball adds. At that
 * yardstick the same measured touches score |quality| ~ 0.61, a hard shot
 * saturates the clamp, and the difference between a good touch and a bad one is
 * 3.6x what it was. It is a NORMALIZER, not a threshold: everything below it
 * still scores proportionally.
 */
const DECISIVE_TOUCH_SPEED = 2000;

/** Boost spent is worth this fraction of boost collected, negated. Collecting
 *  and then spending a pad is therefore a small net positive — boost is for
 *  using — while burning a full tank sitting still is a small net negative. */
const BOOST_WASTE_FRACTION = 0.5;

export const REWARD_TERM_NAMES = [
  'goalScored',
  'goalConceded',
  'ownGoal',
  'ballTouch',
  'ballToOpponentGoalDistance',
  'playerToBallVelocity',
  'boostEconomy',
  'demoDealt',
  'demoReceived',
  'timePenalty',
] as const;

export type RewardTermName = (typeof REWARD_TERM_NAMES)[number];

/**
 * The default weight set.
 *
 * A goal is 10 and every dense term is tuned so that its largest plausible
 * accumulation over a default 2400-tick episode is at most a few units. That
 * ratio is the whole point: dense shaping exists to make the first thousand
 * episodes learnable, and the moment it can out-earn a goal the policy learns
 * to farm it instead of scoring. `test/rl/reward.test.ts` asserts the ratio
 * against measured episode data rather than against this comment.
 */
export const DEFAULT_REWARD_WEIGHTS: Readonly<Record<RewardTermName, number>> = {
  goalScored: 10,
  goalConceded: -10,
  /**
   * ON TOP of `goalConceded` — putting it in yourself is strictly worse than
   * being scored on.
   *
   * Raised from -5. The 12.3M-step run roughly DOUBLED its own-goal rate while
   * its touch rate climbed, which is what a policy rewarded for contact and
   * barely graded on direction converges to. At -8 the culprit's total for an
   * own goal is -18 against +10 for scoring one, so the worst outcome available
   * is unambiguously the worst-paid — and it stays a COUNTING term, so nothing
   * about it can be accumulated a little at a time.
   */
  ownGoal: -8,
  /**
   * Unchanged at 0.25 THROUGH the directional fix, deliberately. What that fix
   * moved is inside the term — the yardstick a touch is measured against and
   * whether the possession bonus is paid on a backwards one — so every caller
   * that supplies its own weight keeps the possession bonus it had and gets the
   * stronger direction signal for free.
   *
   * Still the dense term most able to out-earn a goal — every regained
   * possession pays again, and unlike the distance term below the quality does
   * not telescope across a wall bounce, which is the concrete farm: shove the
   * ball at a wall, let the bounce hand the speed back, shove it again. What
   * bounds it is that the wall's return trip is charged in full by
   * `ballToOpponentGoalDistance`, and `test/rl/reward.test.ts` measures the
   * worst episode a scripted All-Star can actually accumulate rather than
   * trusting that argument.
   */
  ballTouch: 0.25,
  /**
   * Large only because the term is a bounded potential difference; see the
   * header.
   *
   * Halved from 3 when possession attribution was added. Attribution removed
   * the part of the term that was pure noise to the agent, but what is left is
   * still the single biggest per-step quantity in the function — several times
   * the two terms a policy learns FIRST — and standardized advantages divide
   * the small ones down by whatever the large one is doing. Measured over 80
   * iterations of the curriculum: at 3 the mean approach speed ended flat at
   * -0.001, at 1.5 it reached +0.119. The reference All-Star's worst episode
   * accumulates 0.75 here, against a goal worth 10.
   */
  ballToOpponentGoalDistance: 1.5,
  /**
   * Per second at full closing speed.
   *
   * Raised from 0.1. This is the ONLY term that is nonzero before a policy has
   * ever touched the ball, it is not zero-sum between two self-playing agents,
   * and it cannot be farmed — closing speed integrates to a bounded distance,
   * and backing off to re-approach pays the negative first. Its old weight put
   * it an order of magnitude below the terms around it, so it was invisible in
   * a standardized advantage. The reference All-Star's worst episode
   * accumulates 2.08 here against a goal worth 10, so the "a goal outweighs the
   * dense shaping by 3x" property still holds with room to spare.
   */
  playerToBallVelocity: 0.3,
  boostEconomy: 0.2,
  demoDealt: 0.5,
  demoReceived: -0.5,
  /** Per second. Small: it exists to break stalling ties, not to rush. */
  timePenalty: -0.02,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const teamOf = (ctx: RlRewardContext): 0 | 1 => ctx.world.cars[ctx.carIndex].team;

const opponentGoal = (team: 0 | 1): Vec3 => GOAL_CENTER[team === 0 ? 1 : 0];

const distance = (a: Vec3, b: Vec3): number =>
  Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

/** Component of `v` along the unit direction from `from` toward `to`. Zero when
 *  the two points coincide. */
const speedToward = (v: Vec3, from: Vec3, to: Vec3): number => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-6) return 0;
  return (v.x * dx + v.y * dy + v.z * dz) / len;
};

const seconds = (window: RewardWindow): number => window.ticks / TICK_RATE;

// ---------------------------------------------------------------------------
// The terms
// ---------------------------------------------------------------------------

/** COUNTING. Goals scored by the agent's team this window. */
const goalScored = (ctx: RlRewardContext): number => {
  const team = teamOf(ctx);
  let n = 0;
  for (const goal of ctx.events.goals) if (goal.team === team) n++;
  return n;
};

/** COUNTING. Goals conceded by the agent's team this window. */
const goalConceded = (ctx: RlRewardContext): number => {
  const team = teamOf(ctx);
  let n = 0;
  for (const goal of ctx.events.goals) if (goal.team !== team) n++;
  return n;
};

/**
 * COUNTING. Goals conceded where THIS car put the ball in.
 *
 * Keyed off the last touch rather than off `scorer`: the world sets `scorer` to
 * null for any own goal, so `scorer` alone cannot tell which of three
 * defenders did it.
 */
const ownGoal = (ctx: RlRewardContext): number => {
  const team = teamOf(ctx);
  let n = 0;
  for (const goal of ctx.events.goals) {
    if (goal.team !== team && goal.lastTouch === ctx.carIndex) n++;
  }
  return n;
};

/**
 * SIGNED. Touching the ball, scored by how much the ball's speed TOWARD the
 * opponent goal changed across the window.
 *
 * A touch that knocks the ball backwards is worth STRICTLY LESS THAN NOTHING,
 * which is the behaviour that matters: a policy rewarded merely for contact
 * learns to sit under the ball and tap it. That is not a hypothetical — it is
 * what 12.3M steps produced, because the possession bonus below used to be paid
 * on any contact and was larger than the whole directional part. So the bonus
 * is now CONDITIONAL: it is paid only on a touch that did not send the ball
 * backwards, and the sign of this term is therefore the sign of the touch.
 *
 * Reaching the ball is still worth climbing toward from a policy that has never
 * done it — a random touch is directionally symmetric, so the bonus still pays
 * in expectation, just at half the rate.
 *
 * The window's whole velocity change is attributed to the touch. In a
 * two-car challenge that credits some of the opponent's work to this car; the
 * alternative is a per-contact impulse accounting that the world does not
 * expose, and over a training run the misattribution is symmetric noise.
 */
const ballTouch = (ctx: RlRewardContext): number => {
  if (!ctx.events.touched[ctx.carIndex]) return 0;

  const goal = opponentGoal(teamOf(ctx));
  const before = speedToward(ctx.previous.ballVelocity, ctx.previous.ballPosition, goal);
  const after = speedToward(ctx.world.ball.velocity, ctx.world.ball.position, goal);
  const quality = clamp((after - before) / DECISIVE_TOUCH_SPEED, -1, 1);

  const won = !ctx.previous.cars[ctx.carIndex].touchedBall;
  const fresh = won && quality >= 0 ? TOUCH_POSSESSION_BONUS : 0;
  return fresh + quality;
};

/**
 * SIGNED. How much closer to the opponent goal the ball got, CREDITED TO THE
 * CAR WHOSE TOUCH THE BALL IS STILL OBEYING. A potential difference — see the
 * header for why that matters.
 *
 * The attribution is not cosmetic; it is what makes the term usable early.
 * Uncredited, every car is paid (or charged) for every metre the ball travels,
 * including metres it had nothing to do with: the opponent's clearance, a wall
 * bounce, or — on a randomized reset — the velocity the ball was simply handed.
 * Per step that unearned component is several times larger than either term an
 * untrained policy CAN control, and because advantages are standardized before
 * the update, a large uncontrollable component does not just add noise, it
 * divides the controllable signal down. Measured with the curriculum below: the
 * uncredited term left the mean approach speed flat at -0.03 over 60 iterations
 * where the credited one took it to +0.03.
 *
 * The telescoping property survives: within one possession the sum is still
 * (distance when you won it - distance when you lost it), which is the quantity
 * the header's anti-farming argument is about. What is dropped is the part of
 * the sum belonging to somebody else's possession.
 */
const ballToOpponentGoalDistance = (ctx: RlRewardContext): number => {
  const owner = ctx.events.lastTouch;
  // `undefined` means the caller does not track possession; see `RewardWindow`.
  if (owner !== undefined && owner !== ctx.carIndex) return 0;
  const goal = opponentGoal(teamOf(ctx));
  const before = distance(ctx.previous.ballPosition, goal);
  const after = distance(ctx.world.ball.position, goal);
  return (before - after) / ARENA_SPAN;
};

/**
 * SIGNED, PER SECOND. The agent's speed component toward the ball.
 *
 * The earliest useful gradient there is: it is nonzero on the very first
 * episode, before any policy has ever touched the ball, and it points at the
 * one thing every other reward depends on.
 */
const playerToBallVelocity = (ctx: RlRewardContext): number => {
  const car = ctx.world.cars[ctx.carIndex];
  if (car.demolished) return 0;
  const toward = speedToward(car.velocity, car.position, ctx.world.ball.position);
  return clamp(toward / CAR_MAX_SPEED, -1, 1) * seconds(ctx.events);
};

/**
 * SIGNED. Boost collected, minus a fraction of boost burned.
 *
 * Skipped entirely across a demolition: a respawn REPLACES the tank with the
 * spawn amount, and reading that as a pickup or as a burn is meaningless
 * either way.
 */
const boostEconomy = (ctx: RlRewardContext): number => {
  const before = ctx.previous.cars[ctx.carIndex];
  const car = ctx.world.cars[ctx.carIndex];
  if (before.demolished || car.demolished) return 0;

  const gained = ctx.events.boostGained[ctx.carIndex] ?? 0;
  const spent = Math.max(0, before.boostAmount + gained - car.boostAmount);
  return (gained - BOOST_WASTE_FRACTION * spent) / BOOST_MAX;
};

/** COUNTING. Demolitions this car dealt. */
const demoDealt = (ctx: RlRewardContext): number => {
  let n = 0;
  for (const demo of ctx.events.demolitions) if (demo.attacker === ctx.carIndex) n++;
  return n;
};

/** COUNTING. Demolitions this car took. */
const demoReceived = (ctx: RlRewardContext): number => {
  let n = 0;
  for (const demo of ctx.events.demolitions) if (demo.victim === ctx.carIndex) n++;
  return n;
};

/** COUNTING, PER SECOND. Elapsed time. Weighted negative, so a policy that can
 *  score prefers scoring now. */
const timePenalty = (ctx: RlRewardContext): number => seconds(ctx.events);

/** Every term's raw computation, exported so a test can call one in isolation
 *  without building a `RewardFunction`. */
export const REWARD_COMPUTES: Readonly<
  Record<RewardTermName, (ctx: RlRewardContext) => number>
> = {
  goalScored,
  goalConceded,
  ownGoal,
  ballTouch,
  ballToOpponentGoalDistance,
  playerToBallVelocity,
  boostEconomy,
  demoDealt,
  demoReceived,
  timePenalty,
};

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

/** Fresh term objects at the given weights. Fresh because `RewardTerm.weight`
 *  is mutable and two environments must not share one. */
export const makeRewardTerms = (
  weights: Partial<Record<RewardTermName, number>> = {}
): RlRewardTerm[] =>
  REWARD_TERM_NAMES.map((name) => ({
    name,
    weight: weights[name] ?? DEFAULT_REWARD_WEIGHTS[name],
    compute: REWARD_COMPUTES[name],
  }));

/**
 * The terms plus the summation, and nothing else — no per-episode memory, so
 * two environments sharing a config still produce independent rewards.
 */
export class RewardFunction {
  readonly terms: readonly RlRewardTerm[];

  constructor(weights: Partial<Record<RewardTermName, number>> = {}) {
    this.terms = makeRewardTerms(weights);
  }

  /**
   * Weighted total. When `breakdown` is given, each term's WEIGHTED
   * contribution is added into it — added, not assigned, so a caller can
   * accumulate a whole episode into one record.
   */
  evaluate(ctx: RlRewardContext, breakdown?: Record<string, number>): number {
    let total = 0;
    for (const term of this.terms) {
      if (term.weight === 0) continue;
      const value = term.compute(ctx) * term.weight;
      total += value;
      if (breakdown !== undefined) breakdown[term.name] = (breakdown[term.name] ?? 0) + value;
    }
    return total;
  }

  /** Current weights, for a checkpoint or a UI. */
  weights(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const term of this.terms) out[term.name] = term.weight;
    return out;
  }
}

/** An all-zeros breakdown, so a chart has every series from the first frame. */
export const emptyRewardBreakdown = (): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const name of REWARD_TERM_NAMES) out[name] = 0;
  return out;
};
